import { useEffect } from "react";
import { isTauri, invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { useLocation, useNavigate } from "react-router-dom";
import { openCommandPalette, toggleSidebar, toggleContext, toggleHelpDialog } from "./uiState";
import { useRooms } from "./rooms";

interface DesktopRoute { host: string; path: string; query?: string | Record<string, string> | null; }
// OS launch URLs are consumed once per document, including workspace remounts.
let initialLinks: Promise<string[] | null> | null = null;
let initialLinkHandled = false;
// Shells from before the background updater only check when asked.
let legacyUpdateCheck: Promise<void> | null = null;
function checkLegacyShellOnce(): void {
  legacyUpdateCheck ??= invoke("get_update_status").then(() => {}, () => invoke("check_updates").then(() => {}, () => {}));
}
/** A restart now would lose work: an active call or an unsent draft. */
export function restartWouldInterrupt(pathname: string, storage: Storage = window.sessionStorage): boolean {
  if (pathname.startsWith("/calls/")) return true;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith("ailechat:draft:") && storage.getItem(key)) return true;
  }
  return false;
}
export function desktopRoute(route: DesktopRoute): string | null {
  const id = route.path.replace(/^\/+/, "").split("/")[0];
  const query = new URLSearchParams(typeof route.query === "string" ? route.query : route.query || {});
  if (["channels", "rooms"].includes(route.host) && id) return `/rooms/${encodeURIComponent(id)}${query.get("message") ? `?message=${encodeURIComponent(query.get("message")!)}` : ""}`;
  if (route.host === "dm") return id ? `/dm?user=${encodeURIComponent(id)}` : "/dm";
  if (route.host === "tasks") return "/tasks";
  if (route.host === "settings") return "/settings";
  if (route.host === "calls" && id) return `/calls/${encodeURIComponent(id)}${query.get("call") ? `?call=${encodeURIComponent(query.get("call")!)}` : ""}`;
  if (route.host === "quick-search") return `/search?q=${encodeURIComponent(query.get("q") || "")}`;
  return null;
}
export function useDesktopBridge() {
  const navigate = useNavigate();
  const { rooms } = useRooms();
  const unread = rooms.reduce((sum, room) => sum + room.unreadCount, 0);
  useEffect(() => {
    if (!isTauri()) return;
    let disposed = false; const handlers: UnlistenFn[] = [];
    const attach = async () => {
      // Previously distributed clients continue emitting the historical names.
      const subscriptions = await Promise.allSettled(["ailechat", "kakehashi"].flatMap(scheme => [
        listen(`${scheme}://quick-search`, () => { if (!disposed) openCommandPalette(); }),
        listen<{ id: string }>(`${scheme}://menu`, event => {
          if (disposed) return;
          switch (event.payload.id) {
            case "file-new-dm": navigate("/dm"); break;
            case "view-sidebar": toggleSidebar(); break;
            case "view-context-pane": toggleContext(); break;
            case "help-shortcuts": toggleHelpDialog(); break;
          }
        }),
        listen<DesktopRoute>(`${scheme}://route`, event => {
          if (disposed) return;
          const path = desktopRoute(event.payload);
          if (path) { initialLinkHandled = true; navigate(path); }
        }),
        listen<{ kind: string }>(`${scheme}://updater`, event => {
          if (!disposed) window.dispatchEvent(new CustomEvent("ailechat:updater-status", { detail: event.payload }));
        }),
      ]));
      const listening = subscriptions.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
      const failure = subscriptions.find(result => result.status === "rejected");
      if (disposed || failure) {
        listening.forEach(stop => stop());
        if (failure && !disposed) throw failure.reason;
        return;
      }
      handlers.push(...listening);
      checkLegacyShellOnce();
      // OS launch URLs can arrive before the SPA has subscribed to route events.
      // Read them once per document, so later navigation cannot replay them.
      if (!initialLinkHandled) {
        initialLinks ??= invoke<string[] | null>("plugin:deep-link|get_current").catch(() => null);
        const urls = await initialLinks;
        if (disposed || initialLinkHandled) return;
        initialLinkHandled = true;
        for (const value of urls ?? []) {
          try {
            const url = new URL(value);
            if (!["ailechat:", "kakehashi:"].includes(url.protocol) || url.username || url.password || url.port) continue;
            const path = desktopRoute({ host: url.hostname, path: url.pathname, query: url.search });
            if (path) { navigate(path); break; }
          } catch { /* Ignore malformed OS launch URLs. */ }
        }
      }
    };
    void attach().catch(error => console.error("Desktop event subscription failed", error));
    return () => { disposed = true; handlers.forEach(stop => stop()); };
  }, [navigate]);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isTauri()) return;
    // The shell restarts to apply an update only while this reports false.
    const report = () => void invoke("set_update_guard", { busy: restartWouldInterrupt(pathname) }).catch(() => {});
    report();
    const timer = window.setInterval(report, 30_000);
    return () => window.clearInterval(timer);
  }, [pathname]);
  useEffect(() => { if (isTauri()) void invoke("set_unread_count", { count: unread }).catch(error => console.error("Desktop unread count update failed", error)); }, [unread]);
}
export async function desktopNotify(title: string, body: string): Promise<void> {
  if (isTauri()) await invoke("show_notification", { title, body });
}
